<template>
  <div :class="['pie-chart-card', gridClass]">
    <div class="course-card">
      <div class="course-card-body">
        <div class="card-header">
          <h4 class="card-title">
            <i :class="icon"></i>
            <span>{{ title }}</span>
          </h4>
        </div>

        <div class="chart-body">
          <template v-if="loading">
            <div class="skeleton-chart-wrapper">
              <div class="skeleton skeleton-pie-circle"></div>
              <div class="skeleton-legend">
                <div v-for="n in 3" :key="'legend_' + n" class="skeleton-legend-item">
                  <div class="skeleton skeleton-legend-color"></div>
                  <div class="skeleton skeleton-legend-text"></div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="hasValidData">
            <div class="pie-stage">
              <apexchart
                :key="chartRenderKey"
                type="pie"
                :width="pieSize"
                :height="pieSize"
                :options="responsiveChartOptions"
                :series="chartOptions.series"
              />
            </div>

            <ul class="custom-legend">
              <li
                v-for="(item, index) in legendItems"
                :key="'legend_' + index"
                class="custom-legend__item"
                :class="{
                  'custom-legend__item--active': activeIndex === index,
                  'custom-legend__item--muted': activeIndex !== null && activeIndex !== index,
                }"
                role="button"
                tabindex="0"
                @click="toggleLegendItem(index)"
                @keydown.enter.prevent="toggleLegendItem(index)"
                @keydown.space.prevent="toggleLegendItem(index)"
              >
                <span class="custom-legend__swatch" :style="{ background: item.color }"></span>
                <span class="custom-legend__label">{{ item.label }}</span>
                <span class="custom-legend__value">{{ item.value }}</span>
              </li>
            </ul>
          </template>

          <div v-else class="no-data">
            <i class="uil-inbox"></i>
            <p>{{ $t('general.no_result_found') || 'Veri bulunamadı' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const CHART_COLORS = [
  "#ae71ff", "#3e416d", "#2596be", "#36b37e", "#e87a60",
  "#4572A7", "#AA4643", "#89A54E", "#80699B", "#3D96AE",
  "#DB843D", "#92A8CD", "#A47D7C", "#B5CA92",
];

export default {
  name: "PieChartCard",
  props: {
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "uil-chart-pie",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
    width: {
      type: [Number, String],
      default: 420,
    },
    gridClass: {
      type: String,
      default: "chart-card",
    },
    windowWidth: {
      type: Number,
      default: 1200,
    },
  },
  data() {
    return {
      activeIndex: null,
    };
  },
  watch: {
    chartOptions() {
      this.activeIndex = null;
      this.closeToolbarMenu();
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick, true);
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    isSmallMobile() {
      return this.windowWidth < 576;
    },
    pieSize() {
      // Her iki chart için sabit daire boyutu (legend sayısı etkilemesin)
      if (this.isSmallMobile) return 250;
      if (this.isMobile) return 260;
      return 280;
    },
    chartRenderKey() {
      return `pie-${this.pieSize}-${(this.chartOptions?.series || []).length}-a${this.activeIndex}`;
    },
    hasValidData() {
      if (!this.chartOptions) return false;
      if (!Array.isArray(this.chartOptions.series) || !this.chartOptions.series.length) return false;
      if (!this.chartOptions.series.some(value => value > 0)) return false;
      return !!this.chartOptions.chartOptions;
    },
    baseColors() {
      return this.chartOptions?.chartOptions?.colors || CHART_COLORS;
    },
    legendItems() {
      if (!this.hasValidData) return [];

      const series = this.chartOptions.series || [];
      const labels = this.chartOptions.chartOptions?.labels || [];
      const colors = this.baseColors;

      return series.map((value, index) => {
        const rawLabel = String(labels[index] || "");
        const label = rawLabel.length > 36 ? `${rawLabel.slice(0, 34)}…` : rawLabel;
        return {
          label,
          value,
          color: colors[index % colors.length],
        };
      });
    },
    displayColors() {
      const colors = this.baseColors;
      const total = (this.chartOptions?.series || []).length;

      return Array.from({ length: total }, (_, index) => {
        const color = colors[index % colors.length];
        if (this.activeIndex === null || this.activeIndex === index) {
          return color;
        }
        return this.withAlpha(color, 0.28);
      });
    },
    responsiveChartOptions() {
      if (!this.hasValidData) return null;

      const base = this.chartOptions.chartOptions || {};

      return {
        chart: {
          type: "pie",
          width: this.pieSize,
          height: this.pieSize,
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
            export: {
              csv: {
                filename: undefined,
              },
              svg: {
                filename: undefined,
              },
              png: {
                filename: undefined,
              },
            },
          },
          zoom: { enabled: false },
          fontFamily: "inherit",
          animations: { enabled: true },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const index = config?.dataPointIndex;
              if (typeof index === "number") {
                this.toggleLegendItem(index);
              }
            },
          },
        },
        colors: this.displayColors,
        labels: base.labels || [],
        legend: {
          show: false, // legend dışarıda, daire boyutu sabit kalsın
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: this.isSmallMobile ? "11px" : "12px",
            fontWeight: 600,
          },
          dropShadow: { enabled: false },
        },
        plotOptions: {
          pie: {
            expandOnClick: true,
            customScale: 1,
            offsetX: 0,
            offsetY: 0,
          },
        },
        stroke: {
          width: 2,
          colors: ["#fff"],
        },
        tooltip: {
          y: {
            formatter: value => value,
          },
        },
        states: {
          active: {
            filter: {
              type: "none",
            },
          },
        },
        responsive: [],
      };
    },
  },
  methods: {
    toggleLegendItem(index) {
      this.activeIndex = this.activeIndex === index ? null : index;
    },
    handleDocumentClick(event) {
      const root = this.$el;
      if (!root) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const toolbar = root.querySelector(".apexcharts-toolbar");
      if (!toolbar) return;

      // Menü ikonuna veya açık menü içine tıklanırsa kapatma
      if (toolbar.contains(target)) return;

      this.closeToolbarMenu();
    },
    closeToolbarMenu() {
      const root = this.$el;
      if (!root) return;

      const openMenus = root.querySelectorAll(".apexcharts-menu.apexcharts-menu-open");
      openMenus.forEach((menu) => {
        menu.classList.remove("apexcharts-menu-open");
      });
    },
    withAlpha(color, alpha) {
      if (!color) return `rgba(148, 163, 184, ${alpha})`;

      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        const full = hex.length === 3
          ? hex.split("").map(char => char + char).join("")
          : hex;
        const r = parseInt(full.slice(0, 2), 16);
        const g = parseInt(full.slice(2, 4), 16);
        const b = parseInt(full.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }

      return color;
    },
  },
};
</script>

<style scoped>
.pie-chart-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.course-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  overflow: visible;
}

.course-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-width: 0;
}

.card-header {
  min-height: 0 !important;
  height: auto !important;
  padding: 0 0 8px !important;
  margin: 0 0 10px !important;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;
}

.card-title {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.card-title span {
  min-width: 0;
}

.card-title i {
  flex-shrink: 0;
  margin-top: 1px;
  line-height: 1;
  color: var(--backgroundColor, #6366f1);
}

.chart-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  min-width: 0;
}

.pie-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 280px;
  overflow: visible;
}

.pie-stage :deep(.vue-apexcharts),
.pie-stage :deep(.apexcharts-canvas) {
  margin: 0 auto;
  overflow: visible !important;
}

.pie-stage :deep(.apexcharts-toolbar) {
  z-index: 5;
  top: 0 !important;
  right: 0 !important;
  padding: 0;
}

.pie-stage :deep(.apexcharts-menu) {
  min-width: 140px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}

.custom-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 10px;
}

.custom-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.custom-legend__item:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}

.custom-legend__item:active {
  transform: scale(0.98);
}

.custom-legend__item--active {
  border-color: var(--backgroundColor, #666cff);
  background: #eef2ff;
  box-shadow: 0 0 0 2px rgba(102, 108, 255, 0.12);
}

.custom-legend__item--muted {
  opacity: 0.55;
}

.custom-legend__swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.custom-legend__label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: #475569;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-legend__value {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  flex-shrink: 0;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 8px 0;
}

.skeleton-pie-circle {
  width: 250px;
  height: 250px;
  border-radius: 50%;
}

.skeleton-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.skeleton-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skeleton-legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.skeleton-legend-text {
  width: 84px;
  height: 12px;
}

.no-data {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #64748b;
  text-align: center;
}

.no-data i {
  font-size: 2.5rem;
  color: #cbd5e1;
}

.no-data p {
  margin: 0;
  font-size: 0.875rem;
}

.chart-card,
.chart-card-small,
.chart-card-large {
  flex: 1;
  min-width: 0;
}

@media (max-width: 768px) {
  .course-card-body {
    padding: 14px;
  }

  .card-header {
    padding-bottom: 6px !important;
    margin-bottom: 8px !important;
  }

  .card-title {
    font-size: 0.875rem;
  }

  .pie-stage {
    min-height: 260px;
  }

  .custom-legend__label {
    max-width: 150px;
  }
}

@media (max-width: 576px) {
  .course-card-body {
    padding: 14px 12px;
  }

  .card-header {
    padding-bottom: 6px !important;
    margin-bottom: 8px !important;
  }

  .pie-stage {
    min-height: 250px;
  }

  .skeleton-pie-circle {
    width: 220px;
    height: 220px;
  }

  .custom-legend {
    gap: 8px;
  }

  .custom-legend__item {
    padding: 6px 8px;
  }

  .custom-legend__label {
    max-width: 130px;
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .card-title {
    font-size: 16px;
    line-height: 1.4;
  }

  .custom-legend__item {
    min-height: 44px;
    box-sizing: border-box;
  }

  .custom-legend__label,
  .custom-legend__value {
    font-size: 14px;
    line-height: 1.4;
  }
}
</style>
